import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  MedicalVm,
  MedicalListItem,
  MedicalListResponse,
  MedicalCompanyOption,
  MedicalClassOption,
} from '@/api/types/empMedical.types'

export class EmpMedicalService {
  async getMedicalCompanies(): Promise<MedicalCompanyOption[]> {
    const url = endpoints.empMedical.getMedicalCompanies
    const res = await apiClient.get<MedicalCompanyOption[] | { data: MedicalCompanyOption[] }>(url)
    const raw = res.data as any
    if (Array.isArray(raw)) return raw as MedicalCompanyOption[]
    if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data as MedicalCompanyOption[]
    return []
  }

  async getMedicalClasses(): Promise<MedicalClassOption[]> {
    const url = endpoints.empMedical.getMedicalClasses
    const res = await apiClient.get<MedicalClassOption[] | { data: MedicalClassOption[] }>(url)
    const raw = res.data as any
    if (Array.isArray(raw)) return raw as MedicalClassOption[]
    if (raw && typeof raw === 'object' && Array.isArray(raw.data)) return raw.data as MedicalClassOption[]
    return []
  }

  async getAll(branchId: number, pageNumber: number, pageSize: number): Promise<MedicalListResponse> {
    const url = endpoints.empMedical.getAll(branchId, pageNumber, pageSize)
    const res = await apiClient.get<MedicalListResponse | MedicalListItem[] | { data: MedicalListItem[] }>(url)
    const raw = res.data as any
    if (raw && Array.isArray(raw.data)) {
      return {
        data: raw.data,
        totalRecords: raw.totalRecords ?? raw.data.length ?? 0,
        pageNumber: raw.pageNumber ?? pageNumber,
        pageSize: raw.pageSize ?? pageSize,
      }
    }
    if (Array.isArray(raw)) {
      return {
        data: raw as MedicalListItem[],
        totalRecords: (raw as MedicalListItem[]).length,
        pageNumber,
        pageSize,
      }
    }
    return { data: [], totalRecords: 0, pageNumber, pageSize }
  }

  async getById(id: number): Promise<MedicalVm> {
    const url = endpoints.empMedical.getById(id)
    const res = await apiClient.get<MedicalVm | { data: MedicalVm }>(url)
    const raw = res.data as any
    if (raw && typeof raw === 'object' && 'data' in raw) return raw.data as MedicalVm
    return raw as MedicalVm
  }

  async getByEmployeeId(employeeId: number): Promise<MedicalListItem[]> {
    const url = endpoints.empMedical.getByEmployeeId(employeeId)
    const res = await apiClient.get<MedicalListItem[] | { data: MedicalListItem[] }>(url)
    const raw = res.data as any
    if (Array.isArray(raw)) return raw as MedicalListItem[]
    if (raw && typeof raw === 'object' && 'data' in raw && Array.isArray(raw.data)) {
      return raw.data as MedicalListItem[]
    }
    return []
  }

  async save(payload: MedicalVm): Promise<unknown> {
    const url = endpoints.empMedical.save
    const res = await apiClient.post(url, payload)
    return res.data
  }

  async update(payload: MedicalVm): Promise<unknown> {
    const url = endpoints.empMedical.update
    const res = await apiClient.put(url, payload)
    return res.data
  }

  async delete(id: number): Promise<void> {
    const url = endpoints.empMedical.delete(id)
    await apiClient.delete(url)
  }
}

export const empMedicalService = new EmpMedicalService()
